import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { CheckCircle2, CreditCard, Loader2, Save, TriangleAlert } from "lucide-react";
import { chiNotify } from "@/components/ChiNotification";

const surface = {
  background: "rgba(13,18,32,.8)",
  border: "1px solid rgba(255,255,255,.08)",
  borderRadius: ".9rem",
  padding: "1.25rem",
} as const;

const heading = {
  margin: 0,
  color: "#F4F7FA",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: ".95rem",
  fontWeight: 750,
} as const;

const body = {
  color: "#94A3B8",
  fontFamily: "'Inter', sans-serif",
  fontSize: "var(--chi-body-compact)",
  lineHeight: 1.6,
} as const;

/**
 * Owner-managed annual membership price. Only the Stripe price identifier is
 * handled here — never a secret key — so the value can be corrected without a
 * deployment when the Stripe account or mode changes.
 */
export default function MembershipPricePanel() {
  const savedPrice = trpc.members.membershipPriceSetting.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });
  const health = trpc.members.membershipPriceHealth.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (savedPrice.data !== undefined) setDraft(savedPrice.data ?? "");
  }, [savedPrice.data]);

  const save = trpc.members.updateMembershipPriceSetting.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.members.membershipPriceSetting.invalidate(),
        utils.members.membershipPriceHealth.invalidate(),
      ]);
      chiNotify.success("Membership price saved", {
        description: "New membership checkouts will use this price.",
      });
    },
    onError: error =>
      chiNotify.error("Membership price not saved", { description: error.message }),
  });

  const audit = health.data;
  const trimmed = draft.trim();
  const unchanged = trimmed === (savedPrice.data ?? "");

  return (
    <section aria-labelledby="membership-price-heading" style={{ ...surface, marginTop: "1.25rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: ".6rem", marginBottom: ".5rem" }}>
        <CreditCard size={17} color="#F9A8D4" />
        <h2 id="membership-price-heading" style={heading}>Annual membership price</h2>
      </div>

      <p style={{ ...body, margin: "0 0 .9rem" }}>
        The Stripe price ID used for annual membership checkout. It must be a recurring yearly
        price from the same Stripe account and mode as the configured secret key. Paste the price
        ID from the Stripe dashboard — not a payment link.
      </p>

      {health.isLoading ? (
        <p style={{ ...body, display: "flex", alignItems: "center", gap: ".45rem", margin: "0 0 .9rem" }}>
          <Loader2 className="animate-spin" size={14} /> Checking the current price against Stripe…
        </p>
      ) : audit ? (
        <div
          role="status"
          style={{
            ...body,
            display: "flex",
            gap: ".55rem",
            margin: "0 0 .9rem",
            padding: ".7rem .8rem",
            borderRadius: ".6rem",
            background: audit.usable ? "rgba(34,197,94,.08)" : "rgba(244,63,94,.08)",
            border: `1px solid ${audit.usable ? "rgba(34,197,94,.25)" : "rgba(244,63,94,.25)"}`,
            color: audit.usable ? "#BBF7D0" : "#FDA4AF",
          }}
        >
          {audit.usable ? <CheckCircle2 size={15} style={{ flexShrink: 0 }} /> : <TriangleAlert size={15} style={{ flexShrink: 0 }} />}
          <span>
            {audit.usable ? (
              <>
                Verified in Stripe
                {audit.amountCents !== null && audit.currency
                  ? ` — ${(audit.amountCents / 100).toFixed(2)} ${audit.currency.toUpperCase()} per ${audit.interval ?? "period"}`
                  : ""}
                {audit.liveMode === false ? " (test mode)" : ""}
              </>
            ) : (
              audit.problems.join(" ")
            )}
          </span>
        </div>
      ) : null}

      <label htmlFor="membership-price-id" style={{ ...body, display: "block", marginBottom: ".35rem", color: "#CBD5E1", fontWeight: 600 }}>
        Stripe price ID
      </label>
      <div style={{ display: "flex", gap: ".55rem", flexWrap: "wrap" }}>
        <input
          id="membership-price-id"
          value={draft}
          onChange={event => setDraft(event.target.value)}
          placeholder="Enter the Stripe price ID"
          spellCheck={false}
          autoComplete="off"
          maxLength={255}
          style={{
            flex: "1 1 280px",
            minHeight: 40,
            padding: "0 .7rem",
            borderRadius: ".55rem",
            background: "rgba(8,12,24,.72)",
            border: "1px solid rgba(255,255,255,.12)",
            color: "white",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "var(--chi-body-compact)",
          }}
        />
        <button
          type="button"
          disabled={save.isPending || trimmed.length === 0 || unchanged}
          onClick={() => save.mutate({ membershipPriceId: trimmed })}
          style={{
            minHeight: 40,
            display: "inline-flex",
            alignItems: "center",
            gap: ".4rem",
            padding: "0 .9rem",
            border: 0,
            borderRadius: ".55rem",
            background: save.isPending || trimmed.length === 0 || unchanged ? "rgba(148,163,184,.25)" : "#0E7490",
            color: "white",
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: "var(--chi-body-compact)",
            cursor: save.isPending ? "wait" : trimmed.length === 0 || unchanged ? "not-allowed" : "pointer",
          }}
        >
          {save.isPending ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />}
          {save.isPending ? "Saving…" : "Save price"}
        </button>
      </div>

      {savedPrice.data === null && (
        <p style={{ ...body, margin: ".7rem 0 0", color: "#FCD34D" }}>
          No price is saved yet, so checkout falls back to the STRIPE_MEMBERSHIP_PRICE_ID
          environment variable. Saving a value here takes precedence over it.
        </p>
      )}
    </section>
  );
}
